<template>
	<div v-if="!isCreating" class="d-flex w-100 justify-start">
		<v-btn v-test="'project-checklist-add-item'" rounded="pill" elevation="0" color="primary" @click="startCreating">
			Add Checklist Item
		</v-btn>
	</div>
	<div v-else class="d-flex w-100 flex-wrap align-start ga-3">
		<!-- LSP-20477: this field hard-coded 100 while every other checklist field (ChecklistEditor,
		     InlineChecklistEditor) and the mobile app (CHECKLIST_TEXT_LIMIT) use FIELD_LENGTHS.TEXT =
		     125, so web and app disagreed on the same checklist item title. Use the shared constant.
		     No server-unit backstop needed (LSP-16854/§3.3): FIELD_LENGTHS.TEXT is 125 code points and
		     a code point is at most 2 UTF-16 units, so this field can never exceed 250 UTF-16 units —
		     under contact-api's validateChecklistItems (checklists.ts:454), which rejects at UTF-16
		     255. If this cap is ever raised above 127, add VuetifyValidation.ServerUtf16BackstopValidation
		     (src/helpers/Validation.ts) as a rule here. -->
		<CharacterLimitedTextField
			ref="itemNameInput"
			v-model.trim="itemName"
			field-noun="checklist item title"
			hide-details="auto"
			class="mb-0 checklist-item-title"
			:max-length="FIELD_LENGTHS.TEXT"
			hard-cap
			:rules="[rules.required]"
			@keyup.enter="saveRecord">
			<template #label><FieldLabel label="Checklist Item Title (Required)" /></template>
		</CharacterLimitedTextField>
		<div class="d-flex ml-auto" style="margin-top: 5px">
			<!-- LSP-20478: icon-check-heavy is the heavy-stroke checkmark in the liveswitch icon font, so
			     it read as bolder than its icon-close-medium partner. icon-check-big is the regular-weight
			     checkmark already used for status icons across the app. -->
			<v-btn size="large" variant="text" elevation="0" icon @click.stop.prevent="saveRecord">
				<v-icon medium color="black">icon-check-big</v-icon>
			</v-btn>
			<v-btn size="large" variant="text" elevation="0" icon @click.stop.prevent="cancelCreate">
				<v-icon medium color="black">icon-close-medium</v-icon>
			</v-btn>
		</div>
		<v-switch v-model="requiresMedia" label="Require a photo or video" class="w-100"></v-switch>
	</div>
</template>

<script lang="ts">
	import api from "@/api";
	import { getMessageFromError, calculateTimeout } from "@/helpers/utilities";
	import { getApiErrorStatus, isEntityGoneError } from "@/helpers/apiErrorShape";
	import { MAX_CHECKLIST_ITEMS } from "@/constants/checklist";
	import { FIELD_LENGTHS } from "@/helpers/Validation";
	import { useToast } from "vue-toastification";
	import FieldLabel from "@common-ui/components/FieldLabel.vue";

	export default {
		components: {
			FieldLabel
		},
		setup() {
			const toast = useToast();
			return { toast, FIELD_LENGTHS };
		},
		props: {
			checklistId: {
				type: String,
				required: true
			},
			disabled: {
				type: Boolean,
				default: false
			}
		},
		// "gone" (LSP-20291): the checklist was already deleted elsewhere (e.g. on the iPad app).
		emits: ["checklist:updated", "update:isDirty", "cancel", "hide-lucky", "show-lucky", "gone"],
		data() {
			return {
				itemName: "",
				isCreating: false,
				saving: false,
				requiresMedia: false,
				// LSP-19790: id of the visible "couldn't add this item" banner, so pressing Add again
				// replaces it rather than stacking a second copy. This is the path the bug was reported
				// on: the server refuses a duplicate item name ("Duplicate checklist item name \"b\"
				// found." — contact-api checklists.ts), and every press used to mint another toast.
				// Dismissing an id that already expired is a no-op in vue-toastification.
				saveErrorToastId: null as number | string | null,
				rules: {
					required: (value: string) => {
						if (!value || value.trim().length === 0) {
							return "Checklist Item Title is required.";
						}
						return true;
					}
				}
			};
		},
		watch: {
			itemName(newVal: string) {
				if (this.isCreating && newVal.trim().length > 0) {
					this.$emit("update:isDirty", true);
				} else {
					this.$emit("update:isDirty", false);
				}
			}
		},
		beforeUnmount(): void {
			// LSP-16140: ProjectChecklists' v-if unmounts this editor when a media attach starts
			// (isExpectingMediaSelection). A dirty draft would otherwise leave update:isDirty(true)
			// outstanding with no editor to save it, firing the parent's unsaved-changes prompt
			// spuriously. A destroyed editor has no draft — retract the dirty flag. (The route/leave
			// guard reads isDirty BEFORE unmount, so a genuine unsaved draft is still protected.)
			if (this.isCreating && this.itemName.trim().length > 0) {
				this.$emit("update:isDirty", false);
			}
		},
		methods: {
			dismissSaveErrorToast(): void {
				if (this.saveErrorToastId == null) return;
				this.toast.dismiss(this.saveErrorToastId);
				this.saveErrorToastId = null;
			},

			/** Show an add-item failure as the ONLY such banner on screen. */
			showSaveErrorToast(message: string): void {
				this.dismissSaveErrorToast();
				this.saveErrorToastId = this.toast.error(message, { timeout: calculateTimeout(message) });
			},

			reset(): void {
				this.itemName = "";
				this.requiresMedia = false;
				this.isCreating = false;
				// The draft is gone, so a banner about saving it no longer describes anything on screen.
				this.dismissSaveErrorToast();
			},
			startCreating(): void {
				if (this.disabled) {
					const message = `You can add up to ${MAX_CHECKLIST_ITEMS} items per checklist.`;
					this.toast.error(message, { timeout: calculateTimeout(message) });
					return;
				}

				this.isCreating = true;
				this.$emit("hide-lucky");
				this.$nextTick(() => {
					const input = this.$refs.itemNameInput as any;
					if (input && input.focus) {
						input.focus();
					}
				});
			},
			async saveRecord(): Promise<boolean> {
				if (this.saving) return false;
				this.saving = true;
				try {
					const itemInput = this.$refs.itemNameInput as any;
					const trimmedName = this.itemName.trim();
					let isValid = !!itemInput;
					if (itemInput?.validate) {
						const validateResult = itemInput.validate();
						const resolvedValidateResult = await validateResult;
						if (typeof resolvedValidateResult === "boolean") {
							isValid = resolvedValidateResult;
						} else if (Array.isArray(resolvedValidateResult)) {
							isValid = resolvedValidateResult.length === 0;
						} else if (typeof itemInput?.valid === "boolean") {
							isValid = itemInput.valid;
						} else {
							isValid = !!resolvedValidateResult;
						}
					} else if (typeof itemInput?.valid === "boolean") {
						isValid = itemInput.valid;
					}
					if (!isValid || !trimmedName) {
						return false;
					}
					await api.addChecklistItems(this.checklistId, [
						{
							name: trimmedName,
							completed: false,
							requiresMedia: this.requiresMedia
						}
					]);
					this.reset();
					this.$emit("checklist:updated");
					this.$emit("update:isDirty", false);
					return true;
				} catch (err) {
					// LSP-20291: a 404/410 means another device already deleted this checklist. Retrying cannot
					// help, so drop the draft and let the parent refresh the list and explain what happened.
					if (isEntityGoneError(err)) {
						console.warn(
							"ChecklistItemEditor.saveRecord: the checklist is already gone on the server — handing off to the parent",
							{
								checklistId: this.checklistId,
								httpStatus: getApiErrorStatus(err)
							}
						);
						this.reset();
						this.$emit("update:isDirty", false);
						this.$emit("gone", err);
						return false;
					}
					const message = getMessageFromError(err) || "There was an error creating the checklist item. Please try again.";
					console.error(err, {
						extra: {
							file: "ChecklistItemEditor.vue",
							method: "createChecklistItem"
						},
						tags: {
							checklistId: this.checklistId
						}
					});
					this.showSaveErrorToast(message);
					return false;
				} finally {
					this.saving = false;
					this.$emit("show-lucky");
				}
			},
			cancelCreate(): void {
				this.reset();
				this.$emit("cancel");
				this.$emit("update:isDirty", false);
				this.$emit("show-lucky");
			}
		}
	};
</script>

<style scoped lang="scss">
	.checklist-item-title {
		flex: 1 1 0;
		min-width: 0;
	}

	@media #{map.get($display-breakpoints, 'xs')} {
		.checklist-item-title {
			flex-basis: 100%;
		}
	}

	:deep(.v-field-label--floating) {
		white-space: break-spaces;
	}
</style>
